import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import PaymentSuccessPopup from '../../Payment/PaymentSuccessPopup';
import { PlatformSettingsService } from '../../../services/platformSettingsService';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../store/authStore';

interface CompleteTransactionPopupProps {
  open: boolean;
  onClose: () => void;
  order?: {
    id: string;
    product_id: string;
    seller_id: string;
    purchase_price: number;
  } | null;
  onPaymentSuccess?: () => void;
}

const CompleteTransactionPopup: React.FC<CompleteTransactionPopupProps> = ({
  open,
  onClose,
  order,
  onPaymentSuccess,
}) => {
  const { user } = useAuthStore();
  const [vendorEmail, setVendorEmail] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [platformEmail, setPlatformEmail] = useState('');
  const [loadingPlatformEmail, setLoadingPlatformEmail] = useState(false);
  const [loadingVendorEmail, setLoadingVendorEmail] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState<string | undefined>();
  const [successAmount, setSuccessAmount] = useState<number | undefined>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const totalAmountRef = useRef<number>(0);

  // Load default platform payment method and order info when dialog opens
  useEffect(() => {
    if (open) {
      loadDefaultPlatformEmail();
      if (order) {
        // Pre-fill vendor amount from order
        setCustomAmount(order.purchase_price.toString());
        // Load vendor email from seller_id
        loadVendorEmail(order.seller_id);
      } else {
        // Reset to empty if no order
        setCustomAmount('');
        setVendorEmail('');
      }
    }
  }, [open, order]);

  const loadVendorEmail = async (sellerId: string) => {
    if (!sellerId) return;
    
    setLoadingVendorEmail(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('email')
        .eq('id', sellerId)
        .single();

      if (fetchError) {
        console.error('Error loading vendor email:', fetchError);
        setError('Failed to load vendor information. Please enter vendor email manually.');
      } else if (data?.email) {
        setVendorEmail(data.email);
      }
    } catch (err: any) {
      console.error('Error loading vendor email:', err);
      setError('Failed to load vendor information. Please enter vendor email manually.');
    } finally {
      setLoadingVendorEmail(false);
    }
  };

  const loadDefaultPlatformEmail = async () => {
    setLoadingPlatformEmail(true);
    setError(null); // Clear any previous errors
    try {
      const defaultMethod = await PlatformSettingsService.getDefaultPlatformPaymentMethod();
      if (defaultMethod && defaultMethod.payment_method_email) {
        setPlatformEmail(defaultMethod.payment_method_email);
        return; // Success, exit early
      }
      
      // Try to get any PayPal payment method if no default is set
      const allMethods = await PlatformSettingsService.getPlatformPaymentMethods();
      const paypalMethod = allMethods.find(m => m.payment_method_type === 'paypal' && m.payment_method_email);
      if (paypalMethod && paypalMethod.payment_method_email) {
        setPlatformEmail(paypalMethod.payment_method_email);
        return; // Success, exit early
      }
      
      // No platform payment method found
      console.warn('No platform payment method found in database');
      setError('No platform payment method configured. Please set up a platform payment method in admin settings.');
    } catch (error: any) {
      console.error('Error loading default platform payment method:', error);
      // Check if it's an RLS/permission error
      if (error?.code === '42501' || error?.message?.includes('permission') || error?.message?.includes('policy')) {
        setError('Permission denied. Please ensure you are logged in and have access to platform settings.');
      } else if (error?.code === 'PGRST116' || error?.message?.includes('not found')) {
        // This is expected if no data exists yet
        setError('No platform payment method configured. Please set up a platform payment method in admin settings.');
      } else {
        setError(`Failed to load platform payment method: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setLoadingPlatformEmail(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    if (!processing) {
      // Only reset if not from order (order-based payments should keep the data)
      if (!order) {
        setVendorEmail('');
        setCustomAmount('');
      }
      setPlatformEmail('');
      setError(null);
      setShowSuccessPopup(false);
      setSuccessOrderId(undefined);
      setSuccessAmount(undefined);
      onClose();
    }
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    setProcessing(false);
    // Call payment success callback if provided
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
    // Always close the transaction popup when success popup closes
    // Reset all state first
    setVendorEmail('');
    setCustomAmount('');
    setPlatformEmail('');
    setError(null);
    setSuccessOrderId(undefined);
    setSuccessAmount(undefined);
    // Then close the popup
    onClose();
  };

  const validateForm = (): boolean => {
    if (!vendorEmail.trim()) {
      setError('Vendor PayPal email is required');
      return false;
    }

    if (!vendorEmail.includes('@')) {
      setError('Please enter a valid vendor PayPal email address');
      return false;
    }

    const amount = parseFloat(customAmount);
    if (!customAmount || isNaN(amount) || amount <= 0) {
      setError('Please enter a valid custom amount greater than 0');
      return false;
    }

    // Platform email validation - should be loaded from platform settings
    if (!platformEmail.trim()) {
      setError('Platform payment information is not available. Please configure platform payment methods in admin settings.');
      return false;
    }

    if (!platformEmail.includes('@')) {
      setError('Platform payment email is invalid. Please configure a valid platform payment method in admin settings.');
      return false;
    }

    return true;
  };

  const handleCompleteTransaction = async () => {
    // Prevent double-clicks by checking if already processing
    if (processing) {
      return;
    }

    setError(null);

    if (!validateForm()) {
      return;
    }

    // Set processing to true immediately to disable the button
    setProcessing(true);

    try {
      // Import services dynamically
      const { PayPalService } = await import('../../../services/paypalService');
      const { TransactionService } = await import('../../../services/transactionService');
      
      const vendorAmount = parseFloat(customAmount);
      const platformAmount = 0.50; // Always $0.50 for platform
      const totalAmount = vendorAmount + platformAmount;
      totalAmountRef.current = totalAmount;

      // Get current user info for payer details
      const currentUser = user || (await supabase.auth.getUser()).data.user;
      const payerId = currentUser?.id || undefined;
      const payerEmail = currentUser?.email || undefined;

      // Create transaction record first (before PayPal order)
      const transaction = await TransactionService.createTransaction({
        transaction_type: order ? 'payment' : 'test',
        is_test_payment: !order, // Only test payments if no order
        product_id: order?.product_id || null,
        order_id: order?.id || null,
        vendor_email: vendorEmail.trim(),
        platform_email: platformEmail.trim(),
        vendor_amount: vendorAmount,
        platform_amount: platformAmount,
        total_amount: totalAmount,
        status: 'pending',
        payer_id: payerId,
        payer_email: payerEmail,
        metadata: {
          created_from: order ? 'order_payment' : 'test_payment_button',
        },
      });

      // Create PayPal order
      const result = await PayPalService.createPaymentOrder({
        vendorEmail: vendorEmail.trim(),
        vendorAmount,
        platformEmail: platformEmail.trim(),
        platformAmount,
      });

      // Update transaction with PayPal order ID if available
      if (result.success && result.orderId) {
        await TransactionService.updateTransaction(transaction.id, {
          paypal_order_id: result.orderId,
          status: 'pending',
        }).catch(err => console.error('Error updating transaction with order ID:', err));
      }

      if (result.success && result.approvalUrl) {
        // Open PayPal in a new window/popup
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const paypalWindow = window.open(
          result.approvalUrl,
          'PayPal Payment',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (!paypalWindow) {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }

        toast.success('Opening PayPal...');

        // Listen for messages from the popup window
        const messageHandler = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          if (event.data.type === 'PAYMENT_SUCCESS') {
            const orderId = event.data.orderId || result.orderId;
            if (orderId) {
              // Check payment status
              const statusResult = await PayPalService.checkPaymentStatus(orderId);
              if (statusResult.success && statusResult.completed) {
                if (intervalRef.current) clearInterval(intervalRef.current);
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                if (messageHandlerRef.current) {
                  window.removeEventListener('message', messageHandlerRef.current);
                }
                
                // Update transaction record with payer information from PayPal
                const { TransactionService } = await import('../../../services/transactionService');
                await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                  status: 'completed',
                  paypal_status: 'COMPLETED',
                  paypal_order_id: orderId,
                  payer_id: statusResult.payerId || undefined,
                  payer_email: statusResult.payerEmail || undefined,
                }).catch(err => console.error('Error updating transaction:', err));
                
                // Update purchase status if this is an order payment
                if (order?.id) {
                  try {
                    const { PurchasesService } = await import('../../../services/purchasesService');
                    await PurchasesService.updatePaidStatus(order.id, 'succeeded');
                  } catch (err) {
                    console.error('Error updating purchase status:', err);
                  }
                }
                
                setSuccessOrderId(orderId);
                setSuccessAmount(totalAmountRef.current);
                setShowSuccessPopup(true);
                // Keep processing state true until success popup is closed
                // This prevents double-clicks during the success flow
              }
            }
          } else if (event.data.type === 'PAYMENT_CANCELLED') {
            toast.error('Payment was cancelled');
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (messageHandlerRef.current) {
              window.removeEventListener('message', messageHandlerRef.current);
            }
            // Reset processing state when payment is cancelled so user can try again
            setProcessing(false);
          }
        };

        messageHandlerRef.current = messageHandler;
        window.addEventListener('message', messageHandler);

        // Monitor the popup for completion
        intervalRef.current = setInterval(async () => {
          try {
            // Check if window was closed
            if (paypalWindow.closed) {
              if (intervalRef.current) clearInterval(intervalRef.current);
              
              // Wait a moment for PayPal to process
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              // Check payment status
              const orderId = result.orderId;
              if (orderId) {
                try {
                  const statusResult = await PayPalService.checkPaymentStatus(orderId);
                  
                  if (statusResult.success && statusResult.completed) {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    
                    // Update transaction record with payer information from PayPal
                    const { TransactionService } = await import('../../../services/transactionService');
                    await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                      status: 'completed',
                      paypal_status: 'COMPLETED',
                      payer_id: statusResult.payerId || undefined,
                      payer_email: statusResult.payerEmail || undefined,
                    }).catch(err => console.error('Error updating transaction:', err));
                    
                    // Update purchase status if this is an order payment
                    if (order?.id) {
                      try {
                        const { PurchasesService } = await import('../../../services/purchasesService');
                        await PurchasesService.updatePaidStatus(order.id, 'succeeded');
                      } catch (err) {
                        console.error('Error updating purchase status:', err);
                      }
                    }
                    
                    setSuccessOrderId(orderId);
                    setSuccessAmount(totalAmountRef.current);
                    setShowSuccessPopup(true);
                    // Keep processing state true until success popup is closed
                    // This prevents double-clicks during the success flow
                    return;
                  } else if (statusResult.success && statusResult.cancelled) {
                    // Update transaction record
                    const { TransactionService } = await import('../../../services/transactionService');
                    await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                      status: 'cancelled',
                      paypal_status: 'CANCELLED',
                    }).catch(err => console.error('Error updating transaction:', err));
                    
                    toast.error('Payment was cancelled');
                    // Reset processing state when payment is cancelled so user can try again
                    setProcessing(false);
                    return;
                  }
                } catch (statusError) {
                  // If status check fails, assume payment was cancelled (user closed window)
                  console.warn('Status check failed, assuming cancellation:', statusError);
                }
                
                // If we get here, status check didn't return completed or cancelled
                // Assume the user cancelled by closing the window
                try {
                  const { TransactionService } = await import('../../../services/transactionService');
                  if (orderId) {
                    await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                      status: 'cancelled',
                      paypal_status: 'CANCELLED',
                    }).catch(err => console.error('Error updating transaction:', err));
                  }
                } catch (updateError) {
                  console.error('Error updating transaction status:', updateError);
                }
                
                toast.error('Payment was cancelled');
                // Reset processing state when payment is cancelled so user can try again
                setProcessing(false);
              } else {
                // No order ID yet, window closed early - assume cancellation
                toast.error('Payment was cancelled');
                setProcessing(false);
              }
              return;
            }

            // Check if the popup has navigated to our return URL
            try {
              // This will throw if cross-origin, which is expected
              const currentUrl = paypalWindow.location.href;
              
              // If we can access the URL, check if it's our return URL
              if (currentUrl.includes('/payment-success') || currentUrl.includes('token=')) {
                paypalWindow.close();
                if (intervalRef.current) clearInterval(intervalRef.current);
                
                // Extract token from URL if present
                const urlParams = new URLSearchParams(currentUrl.split('?')[1]);
                const token = urlParams.get('token');
                const orderId = result.orderId;
                
                if (token && orderId) {
                  // Execute the payment
                  const executeResult = await PayPalService.executePayment(orderId, token);
                  
                  if (executeResult.success) {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    
                    // Update transaction record with payer information from PayPal
                    const { TransactionService } = await import('../../../services/transactionService');
                    await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                      status: 'completed',
                      paypal_status: 'COMPLETED',
                      payer_id: executeResult.payerId || undefined,
                      payer_email: executeResult.payerEmail || undefined,
                    }).catch(err => console.error('Error updating transaction:', err));
                    
                    // Update purchase status if this is an order payment
                    if (order?.id) {
                      try {
                        const { PurchasesService } = await import('../../../services/purchasesService');
                        await PurchasesService.updatePaidStatus(order.id, 'succeeded');
                      } catch (err) {
                        console.error('Error updating purchase status:', err);
                      }
                    }
                    
                    setSuccessOrderId(orderId);
                    setSuccessAmount(totalAmountRef.current);
                    setShowSuccessPopup(true);
                    // Keep processing state true until success popup is closed
                    // This prevents double-clicks during the success flow
                  } else {
                    toast.error(executeResult.error || 'Failed to complete payment');
                    // Reset processing state on execution failure so user can try again
                    setProcessing(false);
                  }
                } else if (orderId) {
                  // Just check status
                  try {
                    const statusResult = await PayPalService.checkPaymentStatus(orderId);
                    if (statusResult.success && statusResult.completed) {
                      if (intervalRef.current) clearInterval(intervalRef.current);
                      if (timeoutRef.current) clearTimeout(timeoutRef.current);
                      
                      // Update transaction record with payer information from PayPal
                      const { TransactionService } = await import('../../../services/transactionService');
                      await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                        status: 'completed',
                        paypal_status: 'COMPLETED',
                        payer_id: statusResult.payerId || undefined,
                        payer_email: statusResult.payerEmail || undefined,
                      }).catch(err => console.error('Error updating transaction:', err));
                      
                      // Update purchase status if this is an order payment
                      if (order?.id) {
                        try {
                          const { PurchasesService } = await import('../../../services/purchasesService');
                          await PurchasesService.updatePaidStatus(order.id, 'succeeded');
                        } catch (err) {
                          console.error('Error updating purchase status:', err);
                        }
                      }
                      
                      setSuccessOrderId(orderId);
                      setSuccessAmount(totalAmountRef.current);
                      setShowSuccessPopup(true);
                      // Keep processing state true until success popup is closed
                      // This prevents double-clicks during the success flow
                    } else if (statusResult.success && statusResult.cancelled) {
                      // Payment was cancelled
                      if (intervalRef.current) clearInterval(intervalRef.current);
                      if (timeoutRef.current) clearTimeout(timeoutRef.current);
                      
                      const { TransactionService } = await import('../../../services/transactionService');
                      await TransactionService.updateTransactionByPayPalOrderId(orderId, {
                        status: 'cancelled',
                        paypal_status: 'CANCELLED',
                      }).catch(err => console.error('Error updating transaction:', err));
                      
                      toast.error('Payment was cancelled');
                      setProcessing(false);
                    }
                  } catch (statusError) {
                    // If status check fails, log but don't throw - continue monitoring
                    console.warn('Status check error (will continue monitoring):', statusError);
                  }
                }
              }
            } catch (e) {
              // Cross-origin error is expected, continue polling
            }
          } catch (error) {
            console.error('Error monitoring payment:', error);
            // Don't throw errors - just log them and continue
            // If the window is closed, we'll handle it in the next iteration
          }
        }, 1000);

        // Timeout after 10 minutes
        timeoutRef.current = setTimeout(() => {
          if (!paypalWindow.closed) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (messageHandlerRef.current) {
              window.removeEventListener('message', messageHandlerRef.current);
            }
            paypalWindow.close();
            toast.error('Payment timed out. Please try again.');
            // Reset processing state on timeout so user can try again
            setProcessing(false);
          }
        }, 600000);
      } else {
        throw new Error(result.error || 'Failed to create PayPal order');
      }
    } catch (err: any) {
      console.error('Error processing payment:', err);
      setError(err.message || 'Failed to process payment. Please try again.');
      toast.error(err.message || 'Failed to process payment');
      // Only reset processing state on error, not on success (success is handled by success popup)
      // This prevents the button from being re-enabled while payment is still processing
      setProcessing(false);
    }
  };

  return (
    <>
    <Dialog
      open={open && !showSuccessPopup}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', color: 'text.primary', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WalletIcon sx={{ color: '#0070BA' }} />
          <Typography variant="h6">Complete Transaction</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Vendor Payment Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
              Vendor Payment
            </Typography>
            {loadingVendorEmail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading vendor information...
                </Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                label="Vendor PayPal Email"
                type="email"
                value={vendorEmail}
                onChange={(e) => setVendorEmail(e.target.value)}
                placeholder="merchant-xxx@business.example.com"
                disabled={processing || !!order}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                helperText={order ? "Vendor email from order" : "Use a PayPal Sandbox Business account email (create at developer.paypal.com/dashboard/accounts)"}
              />
            )}
            <TextField
              fullWidth
              label="Vendor Amount (USD)"
              type="number"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="0.00"
              disabled={processing || !!order}
              inputProps={{ min: 0.01, step: 0.01 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              helperText={order ? "Amount from order" : "Amount to send to vendor"}
            />
          </Box>

          <Divider />

          {/* Platform Payment Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
              Platform Payment
            </Typography>
            {loadingPlatformEmail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading platform payment information...
                </Typography>
              </Box>
            ) : platformEmail ? (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 112, 186, 0.05)',
                  border: '1px solid rgba(0, 112, 186, 0.2)',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: '140px' }}>
                    Platform PayPal Email:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {platformEmail}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: '140px' }}>
                    Platform Fee:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0070BA', fontSize: '1rem' }}>
                    $0.50 USD
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(244, 67, 54, 0.05)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="error">
                  Platform payment information not available. Please configure platform payment methods in admin settings.
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 112, 186, 0.05)',
              border: '1px solid rgba(0, 112, 186, 0.2)',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Total Amount:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0070BA' }}>
              ${(parseFloat(customAmount || '0') + 0.50).toFixed(2)} USD
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={processing}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCompleteTransaction}
          variant="contained"
          disabled={processing}
          startIcon={
            processing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          sx={{
            background: 'linear-gradient(135deg, #0070BA 0%, #005ea6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #005ea6 0%, #004a8c 100%)',
            },
            '&:disabled': {
              background: 'rgba(0, 112, 186, 0.3)',
            },
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {processing ? 'Processing...' : 'Complete Transaction'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Payment Success Popup */}
    <PaymentSuccessPopup
      open={showSuccessPopup}
      onClose={handleSuccessPopupClose}
      orderId={successOrderId}
      amount={successAmount}
    />
    </>
  );
};

export default CompleteTransactionPopup;

